// src/prototype/pages/EAPersonalizationPage.tsx
// ============================================================
// EA STEP 1: PERSONALIZATION - WIRED TO PRODUCTION MODULES
// 
// Navigation handlers:
// - Profile → navigates to /client-profile with return state
// - Surveys → opens EASurveyOverviewModal (modal overlay)
// - Documents → navigates to /documents with return state
//
// FIX: fetchSurveyProgress now uses correct API response format
// API returns: {strategy: {answered, total, status}, ...}
//
// PHASE 1 HELP SYSTEM: Added HelpPopover icons to:
// - Organization Profile card header
// - Data Intake Surveys card header  
// - Document Upload card header
// - Advanced Personalization section header
// ============================================================

import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { EAJourneyBarFrame } from '../components/EAJourneyBarFrame';
import { EAPersonalizationCard } from '../components/EAPersonalizationCard';
import { AdvancedPersonalizationBand } from '../components/AdvancedPersonalizationBand';
import { EASurveyOverviewModal } from '../components/EASurveyOverviewModal';
import { HelpPopover } from '../components/help/HelpPopover';
import { useAuth } from '@/contexts/AuthContext';
import { useEntitlements } from '../hooks/useEntitlements';
import { useDemoUploads } from '../hooks/useDemoUploads';
import {
  documentsStatus,
  profileStatus,
  surveysStatus,
} from '../../lib/coverage/intakeStatus';
import {
  NAMED_INSTRUMENT_COUNT,
  matchedInstrumentCount,
} from '../../lib/coverage/buildCoverageInputs';
import { OPENING_COVERAGE_STATE } from '../../data/coverage/openingCoverageState';
import { PROFILE_FIELD_BINDINGS } from '../../data/coverage/profileMeasureBindings';
import { SURVEY_MODULE_BINDINGS } from '../../data/coverage/surveyMeasureBindings';
import { showDemoControls } from '../config/demoFlags';

// ============================================================
// API BASE URL
// ============================================================

export default function EAPersonalizationPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { session, sessionLoading } = useAuth();

  // Single entitlement source
  const { isPro } = useEntitlements();

  const userId = session?.user_id || 'admin';

  // Modal state
  const [isSurveyModalOpen, setIsSurveyModalOpen] = useState(false);

  // ── ONE STATUS VOCABULARY, THREE SECTION SHAPES ──────────────────────────
  // Every status and every supporting line below is DERIVED. Profile and Surveys are
  // finite, so they can be Complete; Document Upload is open-ended and cannot be, which
  // the return type of documentsStatus enforces rather than a convention.
  //
  // Profile and Surveys derive from the coverage binding tables, which is where the demo
  // declares which fields carry values and which modules are answered. They are constant
  // today because the seed says so, not because a literal is typed here: change the seed
  // and the cards move.
  const { documents: uploadedDocuments, evidenceSources } = useDemoUploads();

  const profile = profileStatus(
    OPENING_COVERAGE_STATE.populatedProfileFields.length,
    PROFILE_FIELD_BINDINGS.length,
  );
  const surveys = surveysStatus(
    OPENING_COVERAGE_STATE.completedSurveyModules.length,
    SURVEY_MODULE_BINDINGS.length,
  );
  // A NAMED INSTRUMENT IS A PARSED TEMPLATE, not a matched filename: the count exists to
  // separate documents uploaded from documents that were actually read.
  const documents = documentsStatus(
    uploadedDocuments.length,
    matchedInstrumentCount(evidenceSources),
    NAMED_INSTRUMENT_COUNT,
  );

  // ============================================================
  // SCROLL RESTORATION
  // When returning from Profile or Documents pages, restore scroll
  // ============================================================
  useEffect(() => {
    const state = location.state as { restoreScrollY?: number; refresh?: boolean } | null;
    if (state?.restoreScrollY) {
      setTimeout(() => {
        window.scrollTo(0, state.restoreScrollY ?? 0);
      }, 100);
      // Clear state to prevent re-scroll
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate, location.pathname]);

  // ============================================================
  // NAVIGATION HANDLERS
  // ============================================================

  /**
   * Profile: Navigate to ClientProfilePage with return state
   */
  const handleProfileClick = () => {
    console.log('[EA] Navigating to Profile');
    navigate('/client-profile', {
      state: {
        fromEA: true,
        returnTo: '/home',
        restoreScrollY: window.scrollY,
      }
    });
  };

  /**
   * Surveys: Open modal overlay
   */
  const handleSurveysClick = () => {
    console.log('[EA] Opening Survey Modal');
    setIsSurveyModalOpen(true);
  };

  /**
   * Documents: Navigate to DocumentManagementPage with return state
   */
  const handleDocumentsClick = () => {
    console.log('[EA] Navigating to Documents');
    // NO auto-advance here. Opening (or closing) the Documents surface used to call
    // addDocuments(), so the tracker climbed just for navigating — the number moved without
    // anything having been uploaded, which is exactly the kind of thing a prospect notices.
    // Docs completion now changes ONLY on a real upload or delete, wired in
    // DocumentManagementPage.
    navigate('/documents', {
      state: {
        fromEA: true,
        returnTo: '/home',
        restoreScrollY: window.scrollY,
      }
    });
  };

  /**
   * Survey modal close handler.
   *
   * Nothing to recompute on close any more. The modal used to report a survey completion
   * percentage back into the completion store; there is no store and no percentage, and
   * which survey modules are complete is declared in the coverage binding tables.
   */
  const handleSurveyModalClose = () => {
    setIsSurveyModalOpen(false);
  };

  // ============================================================
  // LOADING STATE
  // ============================================================
  if (sessionLoading) {
    return (
      <div className="min-h-screen bg-[#F5F7FA] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#18365E] mx-auto mb-4"></div>
          <p className="text-[#717182]">Loading your session...</p>
        </div>
      </div>
    );
  }

  // ============================================================
  // RENDER
  // ============================================================
  return (
    <div className="min-h-screen bg-[#F5F7FA]">
      {/* Journey Bar */}
      <EAJourneyBarFrame sticky isPro={isPro} />

      {/* Main Content */}
      <main className="px-6 py-4">
        {/* Card Section Labels with Help Icons */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-2">
          <div className="flex items-center gap-1.5">
            <span className="text-[11px] font-semibold text-[#18365E] uppercase tracking-wide">Organization Profile</span>
            <HelpPopover helpKey="organization_profile" />
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-[11px] font-semibold text-[#18365E] uppercase tracking-wide">Data Intake Surveys</span>
            <HelpPopover helpKey="data_intake_surveys" />
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-[11px] font-semibold text-[#18365E] uppercase tracking-wide">Document Upload</span>
            <HelpPopover helpKey="document_upload" />
          </div>
        </div>

        {/* Personalization Hub Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <EAPersonalizationCard
            type="profile"
            status={profile.status}
            detail={profile.detail}
            onClick={handleProfileClick}
          />
          <EAPersonalizationCard
            type="surveys"
            status={surveys.status}
            detail={surveys.detail}
            onClick={handleSurveysClick}
          />
          <EAPersonalizationCard
            type="documents"
            status={documents.status}
            detail={documents.detail}
            onClick={handleDocumentsClick}
          />
        </div>

        {/* Advanced Personalization Section Header with Help */}
        <div className="flex items-center gap-1.5 mb-2">
          <span className="text-[11px] font-semibold text-[#18365E] uppercase tracking-wide">Advanced Personalization</span>
          <HelpPopover helpKey="advanced_personalization" />
        </div>

        {/* Advanced Personalization Band (available to all plans) */}
        <AdvancedPersonalizationBand className="mb-4" />

        {/* Navigation Footer with permission message */}
        <div className="flex flex-col items-end gap-2 pt-3">
          <div className="text-right max-w-md">
            <p className="text-xs text-[#717182] leading-relaxed">
              Your inputs are strong enough to generate meaningful, tailored opportunities.
              <br />
              You can refine further at any time.
            </p>
          </div>

          <div className="flex items-center gap-4">
            {/* Secret reset link - only visible in dev */}
            {showDemoControls() && (
              <button
                onClick={() => {
                  localStorage.setItem('dva_demo_plan_override', 'freemium');
                  window.location.reload();
                }}
                className="text-[10px] text-[#717182]/50 hover:text-[#717182] transition-colors"
              >
                Reset Demo
              </button>
            )}

            {/* CTA Button */}
            <button
              onClick={() => navigate('/opportunities')}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold shadow-lg transition-all bg-[#18365E] text-white hover:bg-[#18365E]/90"
            >
              Continue to Opportunities
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </main>

      {/* Survey Overview Modal */}
      {/* onProgressUpdate is deliberately not passed: it existed to push a survey
          completion percentage into the completion store, and there is no longer a store
          or a percentage for it to push. The prop is optional on the modal. */}
      <EASurveyOverviewModal
        isOpen={isSurveyModalOpen}
        onClose={handleSurveyModalClose}
      />
    </div>
  );
}